import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { useParams } from 'react-router-dom'

import {
    useUserGetUserByIdQuery,
    useCompanyGetNfcTagsForUserQuery,
    useCompanyGetChargePointsForUserQuery,
    useUserGetForUserByNfcAccessWithNfcAccessInfoQuery,
    useUserGetPoolsForSelectedCompanyQuery,
    useCompanyGetPoolsQuery,
    NfcTag,
    useNfcTagUpdateMutation,
    useNfcTagDisconnectFromUserMutation,
} from '../../../common/redux/api/web-api'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import { AdminUserAccessToChargersWidget } from '../../components/admin-user-access-to-chargers-widget'
import { UserRegisteredToChargersWidget } from '../../components/user-registered-to-chargers-widget'
import { AdminUserAccessToPoolsWidget } from '../../components/admin-user-access-to-pools-widget'
import { AdminUserRfidTagsWidget } from '../../components/admin-user-rfid-tags-widget'
import { PageLayoutContainer } from '../../../lib/page-layout-container'
import { setSelectedUser } from '../../redux/features/selectedUser/selectedUserSlice'
import { WidgetLoadingState } from '../../../lib/widget-loading-state'
import hasClaimForCompany from '../../../lib/hasClaimForCompany'
import UserDetails from '../../components/UserDetails'
import { RootState } from '../../redux/store'
import Claims from '../../../lib/claims'
import useText from '../../../common/hooks/useText'

const Column = styled.div`
    display: flex;
    flex-direction: column;
    width: 50%;

    @media screen and (max-width: 800px) {
        width: 100%;
    }
`

const Row = styled.div`
    display: flex;
    flex-direction: row;
    gap: 20px;

    @media screen and (max-width: 800px) {
        flex-direction: column;
    }
`

export const UserPage = React.memo(function UserPage(): React.ReactElement {
    const text = useText()
    const dispatch = useDispatch()
    const { id: userId } = useParams()
    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)
    const signedInUser = useSelector((state: RootState) => state.user)

    /**Fetch data **/
    const { data: user } = useUserGetUserByIdQuery(
        { userId, companyId },
        { skip: !userId, refetchOnMountOrArgChange: true }
    )
    const {
        data: nfcTags,
        refetch: getNfcTags,
        isLoading: loadingNfcTags,
    } = useCompanyGetNfcTagsForUserQuery({ userId, companyId }, { skip: !userId, refetchOnMountOrArgChange: true })
    const { data: registeredChargePoints, isLoading: loadingRegisteredChargePoints } =
        useCompanyGetChargePointsForUserQuery({ companyId, userId }, { skip: !userId, refetchOnMountOrArgChange: true })
    const { data: nfcAccessChargePoints, isLoading: loadingNfcAccessChargePoints } =
        useUserGetForUserByNfcAccessWithNfcAccessInfoQuery(
            { companyId, userId },
            { skip: !userId, refetchOnMountOrArgChange: true }
        )
    const { data: pools, isLoading: loadingPools } = useUserGetPoolsForSelectedCompanyQuery(
        { userId, companyId },
        { skip: !userId, refetchOnMountOrArgChange: true }
    )
    const { data: companyPools, isLoading: loadingCompanyPools } = useCompanyGetPoolsQuery(
        { companyId },
        { skip: !userId, refetchOnMountOrArgChange: true }
    )
    /** Mutations **/
    const [updateNfcTag] = useNfcTagUpdateMutation()
    const [deleteNfcTag] = useNfcTagDisconnectFromUserMutation()
    /***************/

    useEffect(() => {
        if (user) dispatch(setSelectedUser(user))
    }, [dispatch, user])

    const isAdmin = () => {
        return hasClaimForCompany(signedInUser, companyId, Claims.Admin)
    }

    const editNfcTagsGroup = async (tags: NfcTag[]) => {
        try {
            for (let i = 0; i < tags.length; i++) {
                await updateNfcTag({ nfcTag: tags[i] }).unwrap()
            }
            await getNfcTags()
            notifySuccess(text('msgRFIDTagsUpdated'))
        } catch (e) {
            notifyError(text('msgNFCTagNotUpdated'))
        }
    }

    const deleteNfcTagFromUser = async (nfcTag: NfcTag) => {
        try {
            if (nfcTag.id && userId) await deleteNfcTag({ nfcTagId: nfcTag.id, userId }).unwrap()
            await getNfcTags()
            notifySuccess(text('msgNFCTagRemoved', { id: nfcTag.id }))
        } catch (e) {
            notifyError(text('msgRFIDNotRemoved'))
        }
    }

    return (
        <PageLayoutContainer>
            <Row>
                <Column>
                    <UserDetails />
                    {user && (
                        <AdminUserAccessToChargersWidget
                            chargePoints={nfcAccessChargePoints ?? []}
                            isAdmin={isAdmin()}
                            dataLoadingChargePoints={loadingNfcAccessChargePoints}
                        />
                    )}
                    {loadingNfcAccessChargePoints && <WidgetLoadingState headerButton />}
                </Column>
                <Column>
                    {!loadingRegisteredChargePoints && user && (
                        <UserRegisteredToChargersWidget
                            registeredChargePoints={registeredChargePoints ?? []}
                            dataLoadingOwnedChargePoints={loadingRegisteredChargePoints}
                        />
                    )}
                    {loadingRegisteredChargePoints && <WidgetLoadingState />}
                    {!loadingNfcTags && user && (
                        <AdminUserRfidTagsWidget
                            nfcTags={nfcTags ?? []}
                            isAdmin={isAdmin()}
                            editNfcTagsGroup={(tags) => editNfcTagsGroup(tags)}
                            deleteNfcTagFromUser={(tag) => deleteNfcTagFromUser(tag)}
                        />
                    )}
                    {loadingNfcTags && <WidgetLoadingState headerButton />}
                    {!loadingPools && user && user.id && (
                        <AdminUserAccessToPoolsWidget
                            pools={pools ?? []}
                            isAdmin={isAdmin()}
                            dataloadingPools={loadingPools || loadingCompanyPools}
                            disableEdit={companyPools?.length === 0}
                            userId={user.id}
                        />
                    )}
                    {loadingPools && <WidgetLoadingState headerButton />}
                </Column>
            </Row>
        </PageLayoutContainer>
    )
})
